import { P, QuestionSet, type QuestionSetItem, Stack, useText } from "@sveabank/cc-common";
import { useState } from "react";
import Content from "../../components/content";
import { withLoader } from "../../hoc/with-loader";
import { usePageContext } from "../../hooks/context";
import type { ClcProductDefinitionId } from "../../types/clc-application";
import { CancelClcModal } from "./components/cancel-modal";
import { useClcCompanyApplication } from "./hooks";
import ClcAgreement from "./steps/agreement";
import ClcAmlQuestions from "./steps/aml";
import { ClcApplicationQuestions } from "./steps/application-questions";
import ClcBeneficialOwners from "./steps/beneficial-ownership";
import { ClcCompanyInfo } from "./steps/company-info";
import ClcShares from "./steps/company-shares";
import ClcCompanyStructure from "./steps/company-structure";
import ClcPayment from "./steps/payment";
import ClcPepQuestions from "./steps/pep";
import ClcProducts from "./steps/products";
import { areClcProductAmlQuestionsComplete } from "./utils";

const NewCompanyForm = () => {
	const [t, texts] = useText();
	const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);

	const {
		amlQuestions,
		clcApplicationDetails,
		submitClcCompanyRegistration,
		submitClcCompanyShares,
		submitClcBeneficialOwnership,
		submitClcRepresentatives,
		submitClcAmlAnswers,
		submitClcBeneficialOwnerAnswers,
		submitClcApplicationQuestions,
		confirmProductConfig,
		cards,
	} = usePageContext(useClcCompanyApplication);

	const canEditApplication = clcApplicationDetails.status === "Ongoing";

	const shouldDisplayPayment =
		clcApplicationDetails.status === "AwaitingPayment" ||
		clcApplicationDetails.status === "PaymentCompleted";

	const productSectionHeadingMap: Partial<Record<ClcProductDefinitionId, string>> = {
		TRANSAKTIONSKONTO: t(texts.newCompany.transactionAccountSection.title),
		SPARKONTO: t(texts.newCompany.savingsAccountSection.title),
		BANKCERTIFICATE: t(texts.newCompany.amlSection.title),
	};

	const productSectionDescriptionMap: Partial<Record<ClcProductDefinitionId, string>> = {
		TRANSAKTIONSKONTO: t(texts.newCompany.transactionAccountSection.description),
		SPARKONTO: t(texts.newCompany.savingsAccountSection.description),
		BANKCERTIFICATE: t(texts.newCompany.amlSection.description),
	};

	const questions: Array<QuestionSetItem> = [
		{
			component: (
				<ClcCompanyInfo
					onSubmit={submitClcCompanyRegistration}
					onCancel={() => setIsCancelModalOpen(true)}
				/>
			),
			heading: t(texts.newCompany.companyInfo.heading),
			description: t(texts.newCompany.companyInfo.description),
			isCompleted: clcApplicationDetails.companyRegistration.isComplete,
		},
		{
			component: (
				<ClcShares onSubmit={submitClcCompanyShares} onCancel={() => setIsCancelModalOpen(true)} />
			),
			heading: t(texts.newCompany.shares.title),
			description: t(texts.newCompany.shares.description),
			isCompleted: clcApplicationDetails.companyShares.isComplete,
		},
		{
			component: (
				<ClcBeneficialOwners
					onSubmit={submitClcBeneficialOwnership}
					onCancel={() => setIsCancelModalOpen(true)}
				/>
			),
			heading: t(texts.newCompany.beneficialOwnership.title),
			description: t(texts.newCompany.beneficialOwnership.description),
			isCompleted: clcApplicationDetails.beneficialOwners.isComplete,
		},
		{
			component: (
				<ClcCompanyStructure
					onSubmit={submitClcRepresentatives}
					onCancel={() => setIsCancelModalOpen(true)}
				/>
			),
			heading: t(texts.newCompany.companyStructure.title),
			description: t(texts.newCompany.companyStructure.description),
			isCompleted: clcApplicationDetails.representatives.isComplete,
		},
		{
			component: (
				<ClcProducts onSubmit={confirmProductConfig} onCancel={() => setIsCancelModalOpen(true)} />
			),
			heading: t(texts.newCompany.products.productsHeading),
			description: t(texts.newCompany.products.productsDescription),
			isCompleted: cards.isComplete,
		},
		{
			component: (
				<ClcApplicationQuestions
					onSubmit={submitClcApplicationQuestions}
					onCancel={() => setIsCancelModalOpen(true)}
				/>
			),
			heading: t(texts.newCompany.applicationQuestions.title),
			description: t(texts.newCompany.applicationQuestions.description),
			isCompleted: clcApplicationDetails.applicationQuestions.isComplete,
		},
		{
			component: (
				<ClcPepQuestions
					onSubmit={submitClcBeneficialOwnerAnswers}
					onCancel={() => setIsCancelModalOpen(true)}
				/>
			),
			heading: t(texts.newCompany.pepSection.title),
			description: t(texts.newCompany.pepSection.description),
			isCompleted: clcApplicationDetails.beneficialOwnerQuestions.isComplete,
		},
		...amlQuestions
			.slice()
			.sort((a, b) => {
				if (a.productDefinitionId < b.productDefinitionId) {
					return -1;
				}
				if (a.productDefinitionId > b.productDefinitionId) {
					return 1;
				}
				return 0;
			})
			.map((questionSet) => ({
				component: (
					<ClcAmlQuestions
						onCancel={() => setIsCancelModalOpen(true)}
						onSubmit={submitClcAmlAnswers}
						questionSet={questionSet}
					/>
				),
				heading: productSectionHeadingMap[questionSet.productDefinitionId] || "",
				description: productSectionDescriptionMap[questionSet.productDefinitionId] || "",
				isCompleted: areClcProductAmlQuestionsComplete(
					questionSet.productId,
					clcApplicationDetails.productQuestions.value,
				),
			})),
		{
			component: <ClcAgreement onCancel={() => setIsCancelModalOpen(true)} />,
			heading: t(texts.newCompany.agreement.title),
			description: t(texts.newCompany.agreement.description),
			isCompleted: clcApplicationDetails.packageAgreement.isComplete,
		},
		...(shouldDisplayPayment
			? [
					{
						component: <ClcPayment onCancel={() => setIsCancelModalOpen(true)} />,
						heading: t(texts.newCompany.payment.paymentHeading),
						description: t(texts.newCompany.payment.paymentDescription),
						isCompleted: clcApplicationDetails.paymentStatus === "Complete",
					},
				]
			: []),
	];

	return (
		<Content py={4}>
			<Stack gap={8}>
				<P>{t(texts.newCompany.description)}</P>
				<QuestionSet questions={questions} isEditable={canEditApplication} />
			</Stack>
			<CancelClcModal closeModal={() => setIsCancelModalOpen(false)} open={isCancelModalOpen} />
		</Content>
	);
};

export default withLoader(useClcCompanyApplication, NewCompanyForm);
